import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import ImageUpload from "../components/ImageUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { 
  Save, Plus, Trash2, Calendar as CalendarIcon, MapPin, 
  Phone, Mail, Globe, Music, Clock, DollarSign,
  Crown, Check, Tag, CreditCard, Loader2, Ticket, Guitar, Edit,
  Search, Users, ChevronLeft, ChevronRight, X, CheckCircle, XCircle, Bell, Lock,
  BarChart3, Eye, MousePointer, Heart, TrendingUp, Store
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TAGS = [
  { id: "concierto", label: "Concierto", icon: "🎸" },
  { id: "dj", label: "DJ", icon: "🎧" },
  { id: "vermut", label: "Vermut", icon: "🍷" },
  { id: "afterwork", label: "Afterwork", icon: "🍻" },
  { id: "promo", label: "Promo", icon: "🔥" },
  { id: "comida", label: "Comida", icon: "🍔" },
  { id: "afterparty", label: "Afterparty", icon: "🌙" },
  { id: "festival", label: "Festival", icon: "🎪" },
  { id: "acustico", label: "Acústico", icon: "🎵" },
  { id: "jam-session", label: "Jam Session", icon: "🥁" },
  { id: "open-mic", label: "Open Mic", icon: "🎤" },
  { id: "tributo", label: "Tributo", icon: "⭐" },
  { id: "release-party", label: "Release Party", icon: "💿" }
];

const MUSIC_GENRES = [
  { category: "Rock", genres: ["rock", "rock-clasico", "rock-alternativo", "rock-indie", "hard-rock", "rock-progresivo", "rock-psicodelico", "glam-rock", "art-rock"] },
  { category: "Punk", genres: ["punk", "punk-rock", "post-punk", "hardcore-punk", "pop-punk", "skate-punk", "street-punk", "oi", "crust-punk", "punk-77"] },
  { category: "Metal Clásico", genres: ["metal", "heavy-metal", "thrash-metal", "power-metal", "doom-metal", "speed-metal", "gothic-metal"] },
  { category: "Metal Moderno", genres: ["nu-metal", "metalcore", "progressive-metal", "symphonic-metal", "folk-metal", "industrial-metal", "groove-metal", "djent"] },
  { category: "Metal Extremo", genres: ["death-metal", "black-metal", "deathcore", "grindcore", "brutal-death", "melodic-death", "slam"] },
  { category: "Otros", genres: ["grunge", "stoner", "sludge", "post-rock", "noise-rock", "math-rock", "emo", "screamo", "shoegaze", "blues-rock", "rockabilly", "psychobilly"] }
];

const DAYS_OF_WEEK = [
  { id: "lunes", label: "Lunes" },
  { id: "martes", label: "Martes" },
  { id: "miercoles", label: "Miércoles" },
  { id: "jueves", label: "Jueves" },
  { id: "viernes", label: "Viernes" },
  { id: "sabado", label: "Sábado" },
  { id: "domingo", label: "Domingo" }
];

const DEFAULT_OPENING_HOURS = DAYS_OF_WEEK.map(day => ({
  day: day.id,
  open_time: "10:00",
  close_time: "02:00",
  is_closed: false
}));

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bar, setBar] = useState(null);
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [openingHours, setOpeningHours] = useState(DEFAULT_OPENING_HOURS);
  const [editingEvent, setEditingEvent] = useState(null);
  const [approvingEvent, setApprovingEvent] = useState(null);

  // Bar form state
  const [barForm, setBarForm] = useState({
    name: "",
    description: "",
    address: "",
    city_id: "",
    phone: "",
    email: "",
    website: "",
    contact: "",
    latitude: "",
    longitude: "",
    image_url: "",
    show_phone: true,
    show_email: true,
    show_contact: true,
    music_genres: [],
    venue_type: "",
    open_to_concerts: true,
    available_dates: [],
    available_hours: "",
    booking_conditions: ""
  });

  // Event form state
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    event_date: null,
    event_time: "20:00",
    end_time: "",
    image_url: "",
    price: "",
    ticket_url: "",
    ticket_info: "",
    tags: [],
    music_genres: []
  });

  // New city form
  const [newCityName, setNewCityName] = useState("");
  
  // Band search state
  const [bands, setBands] = useState([]);
  const [bandSearchDate, setBandSearchDate] = useState(null);
  const [bandSearchGenres, setBandSearchGenres] = useState([]);
  const [searchingBands, setSearchingBands] = useState(false);
  
  // Calendar view state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [barAvailableDates, setBarAvailableDates] = useState([]);
  
  // Event filter state
  const [eventTagFilter, setEventTagFilter] = useState("all");
  const [showPastEvents, setShowPastEvents] = useState(true);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Get fresh token for API calls - fallback to localStorage if context not yet updated
  const getAuthHeaders = () => {
    const currentToken = token || localStorage.getItem("token");
    return { Authorization: `Bearer ${currentToken}` };
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Check for payment success on mount
  useEffect(() => {
    const checkPayment = async () => {
      const sessionId = searchParams.get("session_id");
      const subscription = searchParams.get("subscription");
      
      if (subscription === "success" && sessionId) {
        setProcessingPayment(true);
        try {
          const res = await axios.get(`${API}/subscription/status/${sessionId}`, { headers: getAuthHeaders() });
          if (res.data.payment_status === "paid") {
            toast.success("¡PAGO COMPLETADO! Bienvenido a Pro");
            fetchData();
          }
        } catch (e) {
          console.error("Error checking payment:", e);
        } finally {
          setProcessingPayment(false);
          // Clean URL
          window.history.replaceState({}, "", "/dashboard");
        }
      } else if (subscription === "cancelled") {
        toast.error("Pago cancelado");
        window.history.replaceState({}, "", "/dashboard");
      }
    };
    
    if (token) checkPayment();
  }, [searchParams, token]);

  const fetchData = async () => {
    try {
      const [barRes, citiesRes, featuresRes] = await Promise.all([
        axios.get(`${API}/bars/user/me`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cities`),
        axios.get(`${API}/bars/me/features`, { headers: getAuthHeaders() }).catch(() => null)
      ]);
      
      setBar(barRes.data);
      if (featuresRes?.data) {
        setPlanFeatures(featuresRes.data);
      }
      setBarForm({
        name: barRes.data.name || "",
        description: barRes.data.description || "",
        address: barRes.data.address || "",
        city_id: barRes.data.city_id || "",
        phone: barRes.data.phone || "",
        email: barRes.data.email || "",
        website: barRes.data.website || "",
        contact: barRes.data.contact || "",
        latitude: barRes.data.latitude?.toString() || "",
        longitude: barRes.data.longitude?.toString() || "",
        image_url: barRes.data.image_url || "",
        show_phone: barRes.data.show_phone !== false,
        show_email: barRes.data.show_email !== false,
        show_contact: barRes.data.show_contact !== false,
        music_genres: barRes.data.music_genres || [],
        venue_type: barRes.data.venue_type || "",
        open_to_concerts: barRes.data.open_to_concerts !== false,
        available_hours: barRes.data.available_hours || "",
        booking_conditions: barRes.data.booking_conditions || ""
      });
      
      // Load available dates
      if (barRes.data.available_dates) {
        setBarAvailableDates(barRes.data.available_dates.map(d => new Date(d)));
      }
      
      // Load opening hours
      if (barRes.data.opening_hours && barRes.data.opening_hours.length > 0) {
        setOpeningHours(barRes.data.opening_hours);
      }
      
      setCities(citiesRes.data);

      // Fetch events
      const eventsRes = await axios.get(`${API}/bars/${barRes.data.bar_id}/events`);
      setEvents(eventsRes.data);
      
      // Fetch pending events from bands
      try {
        const pendingRes = await axios.get(`${API}/bars/me/pending-events`, { headers: getAuthHeaders() });
        setPendingEvents(pendingRes.data);
      } catch (e) {
        console.log("No pending events");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("SOLO LOS BARES PUEDEN ACCEDER AL DASHBOARD");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a feature is enabled
  const hasFeature = (featureName) => {
    if (!planFeatures?.features) return true; // Default to true if not loaded
    const value = planFeatures.features[featureName];
    // Handle numeric values (like max_events_per_month)
    if (typeof value === 'number') {
      return value !== 0;
    }
    return value === true;
  };

  // Get max events per month limit
  const getMaxEvents = () => {
    if (!planFeatures?.features) return -1; // Unlimited
    return planFeatures.features.max_events_per_month || -1;
  };

  // Fetch analytics for Pro bars
  const fetchAnalytics = async () => {
    if (bar?.subscription_plan !== "pro") return;
    
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/bars/me/analytics`, { headers: getAuthHeaders() });
      setAnalytics(res.data);
    } catch (error) {
      console.log("Analytics not available:", error.response?.data?.detail);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Load analytics when bar data is available and is Pro
  useEffect(() => {
    if (bar?.subscription_plan === "pro") {
      fetchAnalytics();
    }
  }, [bar?.subscription_plan]);

  const handleBarSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Build data object - explicitly set fields we want to update
      const data = {
        name: barForm.name,
        description: barForm.description,
        address: barForm.address,
        city_id: barForm.city_id || null,
        phone: barForm.phone,
        email: barForm.email,
        website: barForm.website,
        contact: barForm.contact,
        show_phone: barForm.show_phone,
        show_email: barForm.show_email,
        show_contact: barForm.show_contact,
        music_genres: barForm.music_genres || [],
        venue_type: barForm.venue_type || null,
        open_to_concerts: barForm.open_to_concerts,
        available_hours: barForm.available_hours,
        booking_conditions: barForm.booking_conditions,
        opening_hours: openingHours,
        available_dates: barAvailableDates.map(d => format(d, "yyyy-MM-dd"))
      };

      // Always include image_url if it has a value (even empty to clear it)
      if (barForm.image_url) {
        data.image_url = barForm.image_url;
      }

      // Always include coordinates if they have values
      if (barForm.latitude && barForm.longitude) {
        data.latitude = parseFloat(barForm.latitude);
        data.longitude = parseFloat(barForm.longitude);
      }

      await axios.put(`${API}/bars/me`, data, { headers: getAuthHeaders() });
      toast.success("PERFIL ACTUALIZADO");
      fetchData(); // Reload to confirm changes
    } catch (error) {
      console.error("Error saving bar:", error);
      toast.error(error.response?.data?.detail || "ERROR AL GUARDAR");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    if (!eventForm.event_date) {
      toast.error("SELECCIONA UNA FECHA");
      return;
    }

    try {
      const eventDate = new Date(eventForm.event_date);
      const [hours, minutes] = eventForm.event_time.split(":");
      eventDate.setHours(parseInt(hours), parseInt(minutes));

      let endDate = null;
      if (eventForm.end_time) {
        endDate = new Date(eventForm.event_date);
        const [endHours, endMinutes] = eventForm.end_time.split(":");
        endDate.setHours(parseInt(endHours), parseInt(endMinutes));
      }

      const eventData = {
        title: eventForm.title,
        description: eventForm.description,
        event_date: eventDate.toISOString(),
        end_date: endDate?.toISOString() || null,
        image_url: eventForm.image_url || null,
        price: eventForm.price || null,
        ticket_url: eventForm.ticket_url || null,
        ticket_info: eventForm.ticket_info || null,
        tags: eventForm.tags,
        music_genres: eventForm.music_genres
      };

      if (editingEvent) {
        // Update existing event
        await axios.put(`${API}/events/${editingEvent.event_id}`, eventData, { headers: getAuthHeaders() });
        toast.success("EVENTO ACTUALIZADO");
      } else {
        // Create new event
        await axios.post(`${API}/events`, eventData, { headers: getAuthHeaders() });
        toast.success("EVENTO CREADO");
      }

      setEventDialogOpen(false);
      setEditingEvent(null);
      setEventForm({
        title: "",
        description: "",
        event_date: null,
        event_time: "20:00",
        end_time: "",
        image_url: "",
        price: "",
        ticket_url: "",
        ticket_info: "",
        tags: [],
        music_genres: []
      });
      fetchData();
    } catch (error) {
      toast.error(editingEvent ? "ERROR AL ACTUALIZAR EVENTO" : "ERROR AL CREAR EVENTO");
    }
  };

  const handleEditEvent = (event) => {
    const eventDate = new Date(event.event_date);
    const eventTime = format(eventDate, "HH:mm");
    let endTime = "";
    if (event.end_date) {
      endTime = format(new Date(event.end_date), "HH:mm");
    }
    
    setEditingEvent(event);
    setEventForm({
      title: event.title || "",
      description: event.description || "",
      event_date: eventDate,
      event_time: eventTime,
      end_time: endTime,
      image_url: event.image_url || "",
      price: event.price || "",
      ticket_url: event.ticket_url || "",
      ticket_info: event.ticket_info || "",
      tags: event.tags || [],
      music_genres: event.music_genres || []
    });
    setEventDialogOpen(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿ELIMINAR ESTE EVENTO?")) return;

    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: getAuthHeaders() });
      toast.success("EVENTO ELIMINADO");
      fetchData();
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  const handleCreateCity = async (e) => {
    e.preventDefault();
    if (!newCityName.trim()) return;

    try {
      const res = await axios.post(`${API}/cities`, { name: newCityName.trim() }, { headers: getAuthHeaders() });
      setCities([...cities, res.data]);
      setBarForm({ ...barForm, city_id: res.data.city_id });
      setNewCityName("");
      setCityDialogOpen(false);
      toast.success("CIUDAD AÑADIDA");
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR CIUDAD");
    }
  };

  const handleSearchBands = async () => {
    setSearchingBands(true);
    try {
      const params = new URLSearchParams();
      if (bandSearchDate) {
        params.append("date", format(bandSearchDate, "yyyy-MM-dd"));
      }
      if (bandSearchGenres.length > 0) {
        params.append("genres", bandSearchGenres.join(","));
      }
      
      const res = await axios.get(`${API}/bands/search?${params.toString()}`, { headers: getAuthHeaders() });
      setBands(res.data);
      if (res.data.length === 0) {
        toast.info("NO SE ENCONTRARON BANDAS");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL BUSCAR BANDAS");
    } finally {
      setSearchingBands(false);
    }
  };

  // Get events for a specific day (for calendar)
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.event_date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for padding at start
    const startPadding = (firstDay.getDay() + 6) % 7; // Adjust for Monday start
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Filter and sort events
  const getFilteredEvents = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    let filtered = [...events];
    
    // Filter by tag
    if (eventTagFilter !== "all") {
      filtered = filtered.filter(event => event.tags?.includes(eventTagFilter));
    }
    
    // Filter past events if needed
    if (!showPastEvents) {
      filtered = filtered.filter(event => new Date(event.event_date) >= now);
    }
    
    // Sort by date (most recent first for upcoming, then past)
    filtered.sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
    
    return filtered;
  };

  // Check if event is in the past
  const isEventPast = (eventDate) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return new Date(eventDate) < now;
  };

  // Approve event from band
  const handleApproveEvent = async (eventId) => {
    setApprovingEvent(eventId);
    try {
      await axios.put(`${API}/bars/me/events/${eventId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("EVENTO APROBADO");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL APROBAR");
    } finally {
      setApprovingEvent(null);
    }
  };

  // Reject event from band
  const handleRejectEvent = async (eventId) => {
    if (!window.confirm("¿RECHAZAR ESTE EVENTO?")) return;
    
    setApprovingEvent(eventId);
    try {
      await axios.put(`${API}/bars/me/events/${eventId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("EVENTO RECHAZADO");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL RECHAZAR");
    } finally {
      setApprovingEvent(null);
    }
  };

  // Toggle event publish status
  const handleTogglePublish = async (eventId, currentStatus) => {
    try {
      await axios.put(`${API}/events/${eventId}/publish`, 
        { published: !currentStatus }, 
        { headers: getAuthHeaders() }
      );
      toast.success(currentStatus ? "EVENTO DESPUBLICADO" : "EVENTO PUBLICADO");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR");
    }
  };

  // Toggle bar availability date
  const toggleBarAvailableDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const exists = barAvailableDates.some(d => format(d, "yyyy-MM-dd") === dateStr);
    
    if (exists) {
      setBarAvailableDates(barAvailableDates.filter(d => format(d, "yyyy-MM-dd") !== dateStr));
    } else {
      setBarAvailableDates([...barAvailableDates, date]);
    }
  };

  const isBarDateAvailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return barAvailableDates.some(d => format(d, "yyyy-MM-dd") === dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-[#111] w-1/3" />
            <div className="h-64 bg-[#111]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tighter mb-8">
          DASHBOARD <span className="text-primary">ROCKMAPS</span>
        </h1>

        {/* Pending Approval Banner */}
        {bar?.is_approved === false && (
          <div className="bg-yellow-500/10 border border-yellow-500/50 p-6 mb-8" data-testid="pending-approval-banner">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-yellow-500 mb-2">REGISTRO PENDIENTE DE APROBACIÓN</h3>
                <p className="text-zinc-400 text-sm mb-2">
                  Tu bar está siendo revisado por nuestro equipo. Te notificaremos por email cuando sea aprobado.
                </p>
                <p className="text-zinc-500 text-xs">
                  Mientras tanto, puedes completar tu perfil. Una vez aprobado, tu bar será visible en Rockmaps.
                </p>
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-[#111] border border-zinc-800 rounded-none p-1 flex-wrap gap-1">
            <TabsTrigger 
              value="profile" 
              className="font-mono text-xs sm:text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white px-2 sm:px-3"
              data-testid="tab-profile"
            >
              PERFIL
            </TabsTrigger>
            <TabsTrigger 
              value="events" 
              className="font-mono text-xs sm:text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white px-2 sm:px-3"
              data-testid="tab-events"
            >
              EVENTOS ({events.length})
            </TabsTrigger>
            <TabsTrigger 
              value="calendar" 
              className="font-mono text-xs sm:text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white px-2 sm:px-3"
              data-testid="tab-calendar"
            >
              <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="hidden sm:inline">CALENDARIO</span>
              <span className="sm:hidden">CAL</span>
            </TabsTrigger>
            {/* Hidden tabs for Bars: Availability, Requests, Bands, Plan */}
            {bar?.subscription_plan === "pro" && (
              <TabsTrigger 
                value="analytics" 
                className="font-mono text-xs sm:text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white px-2 sm:px-3"
                data-testid="tab-analytics"
              >
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                ANALYTICS
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <form onSubmit={handleBarSubmit} className="bg-[#111] border border-zinc-800 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="md:col-span-2">
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    NOMBRE DEL BAR *
                  </Label>
                  <Input
                    value={barForm.name}
                    onChange={(e) => setBarForm({ ...barForm, name: e.target.value })}
                    required
                    className="input-ruido w-full"
                    data-testid="bar-name-input"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    DESCRIPCIÓN
                  </Label>
                  <Textarea
                    value={barForm.description}
                    onChange={(e) => setBarForm({ ...barForm, description: e.target.value })}
                    className="input-ruido w-full min-h-[100px] resize-none"
                    placeholder="Cuenta qué hace especial a tu bar..."
                    data-testid="bar-description-input"
                  />
                </div>

                {/* City */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <MapPin className="inline w-3 h-3 mr-1" />
                    CIUDAD
                  </Label>
                  <div className="flex gap-2">
                    <Select 
                      value={barForm.city_id || "none"} 
                      onValueChange={(v) => setBarForm({ ...barForm, city_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="flex-1 bg-transparent border-b-2 border-zinc-700 rounded-none font-mono text-sm" data-testid="bar-city-select">
                        <SelectValue placeholder="Selecciona ciudad" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                        <SelectItem value="none" className="font-mono text-sm rounded-none">
                          SIN CIUDAD
                        </SelectItem>
                        {cities.map((city) => (
                          <SelectItem key={city.city_id} value={city.city_id} className="font-mono text-sm rounded-none">
                            {city.name.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Dialog open={cityDialogOpen} onOpenChange={setCityDialogOpen}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="outline" className="border-zinc-700 rounded-none" data-testid="add-city-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#111] border-zinc-800 rounded-none">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-black text-white">AÑADIR CIUDAD</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateCity} className="space-y-4">
                          <Input
                            value={newCityName}
                            onChange={(e) => setNewCityName(e.target.value)}
                            placeholder="Nombre de la ciudad"
                            className="input-ruido"
                            data-testid="new-city-input"
                          />
                          <Button type="submit" className="w-full btn-ruido bg-primary font-mono" data-testid="create-city-btn">
                            CREAR
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <MapPin className="inline w-3 h-3 mr-1" />
                    DIRECCIÓN
                  </Label>
                  <Input
                    value={barForm.address}
                    onChange={(e) => setBarForm({ ...barForm, address: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="Calle, número, ciudad"
                    data-testid="bar-address-input"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Phone className="inline w-3 h-3 mr-1" />
                    TELÉFONO
                  </Label>
                  <Input
                    value={barForm.phone}
                    onChange={(e) => setBarForm({ ...barForm, phone: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="+34 600 000 000"
                    data-testid="bar-phone-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Mail className="inline w-3 h-3 mr-1" />
                    EMAIL
                  </Label>
                  <Input
                    type="email"
                    value={barForm.email}
                    onChange={(e) => setBarForm({ ...barForm, email: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="contacto@bar.com"
                    data-testid="bar-email-input"
                  />
                </div>

                {/* Website */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Globe className="inline w-3 h-3 mr-1" />
                    WEB
                  </Label>
                  <Input
                    value={barForm.website}
                    onChange={(e) => setBarForm({ ...barForm, website: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="www.mibar.com"
                    data-testid="bar-website-input"
                  />
                </div>

                {/* Contact */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Mail className="inline w-3 h-3 mr-1" />
                    CONTACTO (link o texto)
                  </Label>
                  <Input
                    value={barForm.contact}
                    onChange={(e) => setBarForm({ ...barForm, contact: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="email, Instagram, formulario..."
                    data-testid="bar-contact-input"
                  />
                </div>
              </div>

              {/* Visibility Toggles Section */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  VISIBILIDAD DE CONTACTO
                </h3>
                <p className="text-zinc-500 font-mono text-xs mb-4">
                  Controla qué información de contacto es visible públicamente en tu perfil
                </p>
                <div className="space-y-4 bg-[#0a0a0a] p-4 border border-zinc-800">
                  {/* Show Phone Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-zinc-400" />
                      <div>
                        <span className="text-white text-sm">Teléfono</span>
                        {barForm.phone && (
                          <span className="text-zinc-500 font-mono text-xs ml-2">({barForm.phone})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-500">
                        {barForm.show_phone ? "VISIBLE" : "OCULTO"}
                      </span>
                      <Switch
                        checked={barForm.show_phone}
                        onCheckedChange={(checked) => setBarForm({ ...barForm, show_phone: checked })}
                        data-testid="toggle-show-phone"
                      />
                    </div>
                  </div>

                  {/* Show Email Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-zinc-400" />
                      <div>
                        <span className="text-white text-sm">Email</span>
                        {barForm.email && (
                          <span className="text-zinc-500 font-mono text-xs ml-2">({barForm.email})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-500">
                        {barForm.show_email ? "VISIBLE" : "OCULTO"}
                      </span>
                      <Switch
                        checked={barForm.show_email}
                        onCheckedChange={(checked) => setBarForm({ ...barForm, show_email: checked })}
                        data-testid="toggle-show-email"
                      />
                    </div>
                  </div>

                  {/* Show Contact Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-zinc-400" />
                      <div>
                        <span className="text-white text-sm">Contacto adicional</span>
                        {barForm.contact && (
                          <span className="text-zinc-500 font-mono text-xs ml-2 truncate max-w-[150px] inline-block align-bottom">({barForm.contact})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-500">
                        {barForm.show_contact ? "VISIBLE" : "OCULTO"}
                      </span>
                      <Switch
                        checked={barForm.show_contact}
                        onCheckedChange={(checked) => setBarForm({ ...barForm, show_contact: checked })}
                        data-testid="toggle-show-contact"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Venue Type Section */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Store className="w-5 h-5 text-primary" />
                  TIPO DE LOCAL
                </h3>
                <Select 
                  value={barForm.venue_type || "none"} 
                  onValueChange={(value) => setBarForm({ ...barForm, venue_type: value === "none" ? "" : value })}
                >
                  <SelectTrigger 
                    className="w-full md:w-64 bg-black border-zinc-800 rounded-none font-mono"
                    data-testid="venue-type-select"
                  >
                    <SelectValue placeholder="Seleccionar tipo de local" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                    <SelectItem value="none" className="font-mono text-sm rounded-none">
                      SIN ESPECIFICAR
                    </SelectItem>
                    <SelectItem value="sala-conciertos" className="font-mono text-sm rounded-none">
                      SALA DE CONCIERTOS
                    </SelectItem>
                    <SelectItem value="bar" className="font-mono text-sm rounded-none">
                      BAR
                    </SelectItem>
                    <SelectItem value="cafe" className="font-mono text-sm rounded-none">
                      CAFÉ
                    </SelectItem>
                    <SelectItem value="discoteca" className="font-mono text-sm rounded-none">
                      DISCOTECA
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Music Genres Section */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Guitar className="w-5 h-5 text-primary" />
                  GÉNEROS MUSICALES
                </h3>
                <p className="text-zinc-500 font-mono text-xs mb-4">
                  Selecciona los géneros musicales que mejor definen tu bar
                </p>
                <div className="space-y-3 max-h-48 overflow-y-auto bg-[#0a0a0a] p-4 border border-zinc-800">
                  {MUSIC_GENRES.map((category) => (
                    <div key={category.category}>
                      <p className="font-mono text-[10px] text-primary mb-1">{category.category.toUpperCase()}</p>
                      <div className="flex flex-wrap gap-1">
                        {category.genres.map((genre) => (
                          <button
                            key={genre}
                            type="button"
                            onClick={() => {
                              const newGenres = barForm.music_genres.includes(genre)
                                ? barForm.music_genres.filter(g => g !== genre)
                                : [...barForm.music_genres, genre];
                              setBarForm({ ...barForm, music_genres: newGenres });
                            }}
                            className={`px-2 py-1 font-mono text-[10px] border transition-all ${
                              barForm.music_genres.includes(genre)
                                ? "bg-primary border-primary text-white"
                                : "bg-transparent border-zinc-700 text-zinc-500 hover:border-primary"
                            }`}
                            data-testid={`bar-genre-${genre}`}
                          >
                            {genre.replace(/-/g, " ").toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {barForm.music_genres.length > 0 && (
                  <p className="font-mono text-xs text-zinc-400 mt-2">
                    Seleccionados: {barForm.music_genres.length} género(s)
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* Image Upload */}
                <div className="md:col-span-2">
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    IMAGEN DEL BAR
                  </Label>
                  <ImageUpload
                    value={barForm.image_url}
                    onChange={(url) => setBarForm({ ...barForm, image_url: url })}
                    token={token}
                  />
                </div>

                {/* Coordinates */}
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    LATITUD
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    value={barForm.latitude}
                    onChange={(e) => setBarForm({ ...barForm, latitude: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="41.3851"
                    data-testid="bar-latitude-input"
                  />
                </div>

                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    LONGITUD
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    value={barForm.longitude}
                    onChange={(e) => setBarForm({ ...barForm, longitude: e.target.value })}
                    className="input-ruido w-full"
                    placeholder="2.1734"
                    data-testid="bar-longitude-input"
                  />
                </div>

                {/* Geocode Button */}
                <div className="md:col-span-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      if (!barForm.address) {
                        toast.error("INTRODUCE UNA DIRECCIÓN PRIMERO");
                        return;
                      }
                      try {
                        const res = await axios.post(`${API}/geocode?address=${encodeURIComponent(barForm.address)}`);
                        setBarForm({
                          ...barForm,
                          latitude: res.data.latitude.toString(),
                          longitude: res.data.longitude.toString()
                        });
                        toast.success("UBICACIÓN ENCONTRADA");
                      } catch (error) {
                        toast.error("NO SE PUDO ENCONTRAR LA DIRECCIÓN");
                      }
                    }}
                    className="w-full border-zinc-700 text-zinc-300 hover:text-white hover:border-primary font-mono text-sm"
                    data-testid="geocode-btn"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    OBTENER COORDENADAS DE LA DIRECCIÓN
                  </Button>
                  <p className="font-mono text-xs text-zinc-600 mt-2">
                    Las coordenadas se obtienen automáticamente al guardar si la dirección cambia
                  </p>
                </div>
              </div>

              {/* Opening Hours Section */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  HORARIO DE APERTURA
                </h3>
                <div className="space-y-3">
                  {DAYS_OF_WEEK.map((day, index) => {
                    const dayHours = openingHours.find(h => h.day === day.id) || { day: day.id, open_time: "10:00", close_time: "02:00", is_closed: false };
                    return (
                      <div key={day.id} className="flex items-center gap-4 bg-[#0a0a0a] p-3 border border-zinc-800">
                        <div className="w-24 font-mono text-sm text-zinc-400">{day.label.toUpperCase()}</div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!dayHours.is_closed}
                            onCheckedChange={(checked) => {
                              const newHours = [...openingHours];
                              const idx = newHours.findIndex(h => h.day === day.id);
                              if (idx >= 0) {
                                newHours[idx] = { ...newHours[idx], is_closed: !checked };
                              } else {
                                newHours.push({ day: day.id, open_time: "10:00", close_time: "02:00", is_closed: !checked });
                              }
                              setOpeningHours(newHours);
                            }}
                            data-testid={`hours-switch-${day.id}`}
                          />
                          <span className="font-mono text-xs text-zinc-500 w-16">
                            {dayHours.is_closed ? "CERRADO" : "ABIERTO"}
                          </span>
                        </div>
                        {!dayHours.is_closed && (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              type="time"
                              value={dayHours.open_time || "10:00"}
                              onChange={(e) => {
                                const newHours = [...openingHours];
                                const idx = newHours.findIndex(h => h.day === day.id);
                                if (idx >= 0) {
                                  newHours[idx] = { ...newHours[idx], open_time: e.target.value };
                                } else {
                                  newHours.push({ day: day.id, open_time: e.target.value, close_time: "02:00", is_closed: false });
                                }
                                setOpeningHours(newHours);
                              }}
                              className="input-ruido w-24"
                              data-testid={`hours-open-${day.id}`}
                            />
                            <span className="text-zinc-600">-</span>
                            <Input
                              type="time"
                              value={dayHours.close_time || "02:00"}
                              onChange={(e) => {
                                const newHours = [...openingHours];
                                const idx = newHours.findIndex(h => h.day === day.id);
                                if (idx >= 0) {
                                  newHours[idx] = { ...newHours[idx], close_time: e.target.value };
                                } else {
                                  newHours.push({ day: day.id, open_time: "10:00", close_time: e.target.value, is_closed: false });
                                }
                                setOpeningHours(newHours);
                              }}
                              className="input-ruido w-24"
                              data-testid={`hours-close-${day.id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="btn-ruido bg-primary text-white font-mono px-8"
                  data-testid="save-bar-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="space-y-4">
              {/* Create Event Button */}
              <Dialog open={eventDialogOpen} onOpenChange={(open) => {
                setEventDialogOpen(open);
                if (!open) {
                  setEditingEvent(null);
                  setEventForm({
                    title: "",
                    description: "",
                    event_date: null,
                    event_time: "20:00",
                    end_time: "",
                    image_url: "",
                    price: "",
                    ticket_url: "",
                    tags: [],
                    music_genres: []
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-ruido bg-primary text-white font-mono" data-testid="create-event-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    CREAR EVENTO
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#111] border-zinc-800 rounded-none w-[95vw] max-w-lg max-h-[85vh] overflow-y-auto mx-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-black text-white">
                      {editingEvent ? "EDITAR EVENTO" : "NUEVO EVENTO"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4 pb-4">
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">TÍTULO *</Label>
                      <Input
                        value={eventForm.title}
                        onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                        required
                        className="input-ruido w-full"
                        placeholder="Nombre del evento"
                        data-testid="event-title-input"
                      />
                    </div>

                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                      <Textarea
                        value={eventForm.description}
                        onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                        className="input-ruido w-full min-h-[80px] resize-none"
                        placeholder="Describe el evento..."
                        data-testid="event-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                          <CalendarIcon className="inline w-3 h-3 mr-1" />
                          FECHA *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              type="button"
                              variant="outline" 
                              className="w-full justify-start bg-transparent border-b-2 border-zinc-700 rounded-none font-mono text-sm"
                              data-testid="event-date-picker"
                            >
                              {eventForm.event_date 
                                ? format(eventForm.event_date, "d MMM yyyy", { locale: es })
                                : "Seleccionar"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#111] border-zinc-800 rounded-none">
                            <Calendar
                              mode="single"
                              selected={eventForm.event_date}
                              onSelect={(date) => setEventForm({ ...eventForm, event_date: date })}
                              locale={es}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                          <Clock className="inline w-3 h-3 mr-1" />
                          HORA INICIO *
                        </Label>
                        <Input
                          type="time"
                          value={eventForm.event_time}
                          onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                          required
                          className="input-ruido w-full"
                          data-testid="event-time-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                          <Clock className="inline w-3 h-3 mr-1" />
                          HORA FIN
                        </Label>
                        <Input
                          type="time"
                          value={eventForm.end_time}
                          onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                          className="input-ruido w-full"
                          data-testid="event-end-time-input"
                        />
                      </div>

                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                          <DollarSign className="inline w-3 h-3 mr-1" />
                          PRECIO
                        </Label>
                        <Input
                          value={eventForm.price}
                          onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
                          className="input-ruido w-full"
                          placeholder="Gratis / 10€"
                          data-testid="event-price-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                        IMAGEN DEL EVENTO
                      </Label>
                      <ImageUpload
                        value={eventForm.image_url}
                        onChange={(url) => setEventForm({ ...eventForm, image_url: url })}
                        token={token}
                      />
                    </div>

                    {/* Ticket URL - only show if price is set */}
                    {eventForm.price && eventForm.price.toLowerCase() !== "gratis" && (
                      <div className="space-y-4">
                        <div>
                          <Label className="font-mono text-xs text-zinc-500 mb-2 flex items-center gap-2">
                            <Ticket className="inline w-3 h-3" />
                            LINK COMPRA ENTRADAS
                            {!hasFeature("ticket_link") && (
                              <span className="bg-yellow-500/20 text-yellow-500 text-[10px] px-2 py-0.5 font-mono flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                PRO
                              </span>
                            )}
                          </Label>
                          <Input
                            value={eventForm.ticket_url}
                            onChange={(e) => setEventForm({ ...eventForm, ticket_url: e.target.value })}
                            className="input-ruido w-full"
                            placeholder={hasFeature("ticket_link") ? "https://entradas.ejemplo.com/..." : "Disponible con plan Backstage"}
                            disabled={!hasFeature("ticket_link")}
                            data-testid="event-ticket-url-input"
                          />
                        </div>
                        <div>
                          <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                            INFO ENTRADAS (texto adicional)
                          </Label>
                          <Input
                            value={eventForm.ticket_info}
                            onChange={(e) => setEventForm({ ...eventForm, ticket_info: e.target.value })}
                            className="input-ruido w-full"
                            placeholder="Ej: Venta en taquilla desde 18h"
                            data-testid="event-ticket-info-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Tags Selection */}
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                        <Tag className="inline w-3 h-3 mr-1" />
                        TIPO DE EVENTO
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {EVENT_TAGS.map((tag) => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => {
                              const newTags = eventForm.tags.includes(tag.id)
                                ? eventForm.tags.filter(t => t !== tag.id)
                                : [...eventForm.tags, tag.id];
                              setEventForm({ ...eventForm, tags: newTags });
                            }}
                            className={`px-3 py-1.5 font-mono text-xs border transition-all ${
                              eventForm.tags.includes(tag.id)
                                ? "bg-primary border-primary text-white"
                                : "bg-transparent border-zinc-700 text-zinc-400 hover:border-primary"
                            }`}
                            data-testid={`event-tag-${tag.id}`}
                          >
                            <span className="mr-1">{tag.icon}</span>
                            {tag.label.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Music Genres Selection */}
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                        <Guitar className="inline w-3 h-3 mr-1" />
                        GÉNERO MUSICAL
                      </Label>
                      <div className="space-y-3 max-h-40 overflow-y-auto">
                        {MUSIC_GENRES.map((category) => (
                          <div key={category.category}>
                            <p className="font-mono text-[10px] text-primary mb-1">{category.category}</p>
                            <div className="flex flex-wrap gap-1">
                              {category.genres.map((genre) => (
                                <button
                                  key={genre}
                                  type="button"
                                  onClick={() => {
                                    const newGenres = eventForm.music_genres.includes(genre)
                                      ? eventForm.music_genres.filter(g => g !== genre)
                                      : [...eventForm.music_genres, genre];
                                    setEventForm({ ...eventForm, music_genres: newGenres });
                                  }}
                                  className={`px-2 py-1 font-mono text-[10px] border transition-all ${
                                    eventForm.music_genres.includes(genre)
                                      ? "bg-primary border-primary text-white"
                                      : "bg-transparent border-zinc-700 text-zinc-500 hover:border-primary"
                                  }`}
                                  data-testid={`event-genre-${genre}`}
                                >
                                  {genre.replace(/-/g, " ").toUpperCase()}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full btn-ruido bg-primary text-white font-mono"
                      data-testid="submit-event-btn"
                    >
                      {editingEvent ? "GUARDAR CAMBIOS" : "CREAR EVENTO"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Event Filters */}
              <div className="bg-[#111] border border-zinc-800 p-4 mb-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="font-mono text-xs text-zinc-500">TIPO:</Label>
                    <Select value={eventTagFilter} onValueChange={setEventTagFilter}>
                      <SelectTrigger className="w-[150px] bg-transparent border-zinc-700 rounded-none font-mono text-xs">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                        <SelectItem value="all" className="font-mono text-xs">TODOS</SelectItem>
                        {EVENT_TAGS.map(tag => (
                          <SelectItem key={tag.id} value={tag.id} className="font-mono text-xs">
                            {tag.icon} {tag.label.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={showPastEvents}
                      onCheckedChange={setShowPastEvents}
                      id="show-past"
                    />
                    <Label htmlFor="show-past" className="font-mono text-xs text-zinc-500 cursor-pointer">
                      MOSTRAR PASADOS
                    </Label>
                  </div>
                  <span className="font-mono text-xs text-zinc-600 ml-auto">
                    {getFilteredEvents().length} evento(s)
                  </span>
                </div>
              </div>

              {/* Events List */}
              {getFilteredEvents().length > 0 ? (
                <div className="space-y-3">
                  {getFilteredEvents().map((event) => {
                    const isPast = isEventPast(event.event_date);
                    return (
                      <div 
                        key={event.event_id}
                        className={`bg-[#111] border p-4 flex items-center justify-between transition-all ${
                          isPast 
                            ? "border-zinc-900 opacity-50" 
                            : "border-zinc-800"
                        }`}
                        data-testid={`dashboard-event-${event.event_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 flex flex-col items-center justify-center ${
                            isPast ? "bg-zinc-700" : "bg-primary"
                          }`}>
                            <span className="text-lg font-black text-white">
                              {format(new Date(event.event_date), "d")}
                            </span>
                            <span className="text-[10px] font-mono text-white/80 uppercase">
                              {format(new Date(event.event_date), "MMM", { locale: es })}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`font-bold ${isPast ? "text-zinc-500" : "text-white"}`}>
                                {event.title}
                              </h3>
                              {isPast && (
                                <span className="bg-zinc-800 text-zinc-500 text-[9px] font-mono px-1.5 py-0.5">
                                  PASADO
                                </span>
                              )}
                              {event.published === false && (
                                <span className="bg-yellow-500/20 text-yellow-500 text-[9px] font-mono px-1.5 py-0.5">
                                  BORRADOR
                                </span>
                              )}
                            </div>
                            <p className="font-mono text-xs text-zinc-500">
                              {format(new Date(event.event_date), "EEEE d MMM · HH:mm", { locale: es })}H
                              {event.price && ` · ${event.price}`}
                            </p>
                            {event.tags?.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {event.tags.map((tag) => (
                                  <span 
                                    key={tag} 
                                    className={`text-[10px] font-mono px-1.5 py-0.5 uppercase ${
                                      isPast 
                                        ? "bg-zinc-800 text-zinc-500" 
                                        : "bg-primary/20 text-primary"
                                    }`}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePublish(event.event_id, event.published !== false)}
                            className={`text-xs font-mono ${event.published === false ? "text-yellow-500 hover:text-yellow-400" : "text-green-500 hover:text-green-400"}`}
                            title={event.published === false ? "Publicar" : "Despublicar"}
                          >
                            {event.published === false ? "PUBLICAR" : "PÚBLICO"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditEvent(event)}
                            className="text-zinc-500 hover:text-primary hover:bg-primary/10"
                            data-testid={`edit-event-${event.event_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.event_id)}
                            className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                            data-testid={`delete-event-${event.event_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-[#111] border border-zinc-800 p-12 text-center">
                  <Music className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO TIENES EVENTOS PUBLICADOS</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black text-white flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                  CALENDARIO DE EVENTOS
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
                    className="text-zinc-400 hover:text-white"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <span className="font-mono text-sm text-white min-w-[120px] text-center">
                    {format(calendarMonth, "MMMM yyyy", { locale: es }).toUpperCase()}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
                    className="text-zinc-400 hover:text-white"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"].map(day => (
                  <div key={day} className="text-center font-mono text-xs text-zinc-500 py-2">
                    {day}
                  </div>
                ))}
                
                {/* Calendar days */}
                {getCalendarDays().map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }
                  
                  const dayEvents = getEventsForDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div
                      key={date.toISOString()}
                      className={`aspect-square border p-1 ${
                        isToday ? "border-primary" : "border-zinc-800"
                      } ${dayEvents.length > 0 ? "bg-primary/10" : "bg-[#0a0a0a]"}`}
                    >
                      <div className={`text-xs font-mono mb-1 ${isToday ? "text-primary" : "text-zinc-400"}`}>
                        {date.getDate()}
                      </div>
                      {dayEvents.slice(0, 2).map(event => (
                        <div
                          key={event.event_id}
                          className="text-[8px] font-mono bg-primary text-white px-1 py-0.5 truncate mb-0.5 cursor-pointer hover:bg-primary/80"
                          title={event.title}
                          onClick={() => handleEditEvent(event)}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-[8px] font-mono text-zinc-500">
                          +{dayEvents.length - 2} más
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Events for selected month */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <h3 className="font-mono text-sm text-zinc-400 mb-4">
                  EVENTOS ESTE MES ({events.filter(e => {
                    const d = new Date(e.event_date);
                    return d.getMonth() === calendarMonth.getMonth() && d.getFullYear() === calendarMonth.getFullYear();
                  }).length})
                </h3>
                <div className="space-y-2">
                  {events
                    .filter(e => {
                      const d = new Date(e.event_date);
                      return d.getMonth() === calendarMonth.getMonth() && d.getFullYear() === calendarMonth.getFullYear();
                    })
                    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
                    .map(event => (
                      <div
                        key={event.event_id}
                        className="flex items-center justify-between bg-[#0a0a0a] border border-zinc-800 p-3"
                      >
                        <div className="flex items-center gap-3">
                          <div className="font-mono text-sm text-primary">
                            {format(new Date(event.event_date), "d MMM", { locale: es })}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{event.title}</p>
                            <p className="font-mono text-xs text-zinc-500">
                              {format(new Date(event.event_date), "HH:mm")}H
                              {event.ticket_url && " · Entradas disponibles"}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditEvent(event)}
                            className="text-zinc-500 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.event_id)}
                            className="text-zinc-500 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Bands Search Tab */}
          <TabsContent value="bands">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                BUSCAR BANDAS
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Encuentra bandas disponibles para actuar en tu local
              </p>
              
              {/* Search Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <CalendarIcon className="inline w-3 h-3 mr-1" />
                    FECHA DE DISPONIBILIDAD
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start bg-transparent border-b-2 border-zinc-700 rounded-none font-mono text-sm"
                      >
                        {bandSearchDate 
                          ? format(bandSearchDate, "d MMM yyyy", { locale: es })
                          : "Cualquier fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#111] border-zinc-800 rounded-none">
                      <Calendar
                        mode="single"
                        selected={bandSearchDate}
                        onSelect={setBandSearchDate}
                        locale={es}
                      />
                    </PopoverContent>
                  </Popover>
                  {bandSearchDate && (
                    <button
                      onClick={() => setBandSearchDate(null)}
                      className="font-mono text-xs text-zinc-500 hover:text-primary mt-1"
                    >
                      Limpiar fecha
                    </button>
                  )}
                </div>
                
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Guitar className="inline w-3 h-3 mr-1" />
                    GÉNEROS MUSICALES
                  </Label>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto bg-[#0a0a0a] p-2 border border-zinc-800">
                    {MUSIC_GENRES.flatMap(cat => cat.genres).slice(0, 15).map((genre) => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => {
                          const newGenres = bandSearchGenres.includes(genre)
                            ? bandSearchGenres.filter(g => g !== genre)
                            : [...bandSearchGenres, genre];
                          setBandSearchGenres(newGenres);
                        }}
                        className={`px-2 py-1 font-mono text-[10px] border transition-all ${
                          bandSearchGenres.includes(genre)
                            ? "bg-primary border-primary text-white"
                            : "bg-transparent border-zinc-700 text-zinc-500 hover:border-primary"
                        }`}
                      >
                        {genre.replace(/-/g, " ").toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleSearchBands}
                disabled={searchingBands}
                className="btn-ruido bg-primary text-white font-mono mb-6"
              >
                {searchingBands ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    BUSCANDO...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    BUSCAR BANDAS
                  </>
                )}
              </Button>
              
              {/* Results */}
              {bands.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="font-mono text-sm text-zinc-400">
                    {bands.length} BANDA(S) ENCONTRADA(S)
                  </h3>
                  {bands.map((band) => (
                    <div
                      key={band.band_id}
                      className="bg-[#0a0a0a] border border-zinc-800 p-4"
                    >
                      <div className="flex items-start gap-4">
                        {band.image_url ? (
                          <img
                            src={band.image_url}
                            alt={band.name}
                            className="w-16 h-16 object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center">
                            <Music className="w-6 h-6 text-zinc-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-bold text-white">{band.name}</h4>
                          {band.description && (
                            <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{band.description}</p>
                          )}
                          {band.music_genres?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {band.music_genres.map((genre) => (
                                <span
                                  key={genre}
                                  className="bg-zinc-800 text-zinc-300 text-[10px] font-mono px-1.5 py-0.5"
                                >
                                  {genre.replace(/-/g, " ").toUpperCase()}
                                </span>
                              ))}
                            </div>
                          )}
                          {band.available_dates?.length > 0 && (
                            <p className="font-mono text-xs text-primary mt-2">
                              Disponible: {band.available_dates.slice(0, 3).map(d => 
                                format(new Date(d), "d MMM", { locale: es })
                              ).join(", ")}
                              {band.available_dates.length > 3 && ` +${band.available_dates.length - 3} más`}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          {band.email && (
                            <a
                              href={`mailto:${band.email}`}
                              className="inline-flex items-center gap-1 text-primary font-mono text-xs hover:underline"
                            >
                              <Mail className="w-3 h-3" />
                              CONTACTAR
                            </a>
                          )}
                          {band.phone && (
                            <p className="font-mono text-xs text-zinc-500 mt-1">
                              <Phone className="w-3 h-3 inline mr-1" />
                              {band.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border border-zinc-800 bg-[#0a0a0a]">
                  <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">
                    USA LOS FILTROS PARA BUSCAR BANDAS DISPONIBLES
                  </p>
                  <p className="text-zinc-600 text-xs mt-2">
                    Próximamente podrás ver bandas registradas en la plataforma
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Bar Availability Tab */}
          <TabsContent value="availability">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                DISPONIBILIDAD PARA BANDAS
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Selecciona las fechas en las que aceptas propuestas de bandas. Define tus condiciones de reserva.
              </p>

              {/* Open to concerts toggle */}
              <div className="bg-zinc-900/50 border border-zinc-700 p-4 mb-6 flex items-center justify-between">
                <div>
                  <h3 className="font-mono text-white text-sm">ABIERTO A CONCIERTOS</h3>
                  <p className="text-zinc-500 text-xs mt-1">
                    {barForm.open_to_concerts 
                      ? "Las bandas pueden ver tu local y contactarte" 
                      : "Tu local no aparece en la búsqueda de bandas"}
                  </p>
                </div>
                <button
                  onClick={() => setBarForm({...barForm, open_to_concerts: !barForm.open_to_concerts})}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    barForm.open_to_concerts ? "bg-primary" : "bg-zinc-700"
                  }`}
                  data-testid="toggle-open-concerts"
                >
                  <span 
                    className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      barForm.open_to_concerts ? "translate-x-8" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {barForm.open_to_concerts && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
                      className="text-zinc-400 hover:text-white"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <span className="font-mono text-sm text-white">
                      {format(calendarMonth, "MMMM yyyy", { locale: es }).toUpperCase()}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
                      className="text-zinc-400 hover:text-white"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"].map(day => (
                      <div key={day} className="text-center font-mono text-xs text-zinc-500 py-2">
                        {day}
                      </div>
                    ))}
                    
                    {getCalendarDays().map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} className="aspect-square" />;
                      }
                      
                      const isToday = date.toDateString() === new Date().toDateString();
                      const isAvailable = isBarDateAvailable(date);
                      const dayEvents = getEventsForDate(date);
                      const isPast = date < new Date(new Date().setHours(0,0,0,0));
                      
                      return (
                        <button
                          key={date.toISOString()}
                          onClick={() => !isPast && toggleBarAvailableDate(date)}
                          disabled={isPast}
                          className={`aspect-square border p-1 transition-all ${
                            isPast 
                              ? "border-zinc-900 bg-zinc-900/50 cursor-not-allowed" 
                              : isAvailable 
                                ? "border-green-500 bg-green-500/20 hover:bg-green-500/30" 
                                : "border-zinc-800 bg-[#0a0a0a] hover:border-zinc-600"
                          } ${isToday ? "ring-1 ring-primary" : ""}`}
                        >
                          <div className={`text-xs font-mono ${
                            isPast ? "text-zinc-700" : isAvailable ? "text-green-500" : "text-zinc-400"
                          }`}>
                            {date.getDate()}
                          </div>
                          {isAvailable && !isPast && (
                            <Check className="w-3 h-3 text-green-500 mx-auto mt-0.5" />
                          )}
                          {dayEvents.length > 0 && (
                            <div className="text-[8px] font-mono bg-primary text-white px-1 mt-0.5 truncate">
                              {dayEvents.length}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  <p className="font-mono text-xs text-zinc-500 mt-4">
                    <Check className="w-3 h-3 inline text-green-500 mr-1" />
                    {barAvailableDates.length} fecha(s) disponible(s)
                  </p>
                </div>

                {/* Booking Conditions */}
                <div className="space-y-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                      <Clock className="inline w-3 h-3 mr-1" />
                      HORARIOS DISPONIBLES
                    </Label>
                    <Input
                      value={barForm.available_hours}
                      onChange={(e) => setBarForm({ ...barForm, available_hours: e.target.value })}
                      className="input-ruido"
                      placeholder="Ej: Viernes y sábados de 22:00 a 03:00"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                      CONDICIONES DE RESERVA
                    </Label>
                    <Textarea
                      value={barForm.booking_conditions}
                      onChange={(e) => setBarForm({ ...barForm, booking_conditions: e.target.value })}
                      className="bg-transparent border-2 border-zinc-700 rounded-none min-h-[150px]"
                      placeholder="Describe tus condiciones: aforo, equipo disponible, reparto de taquilla, requisitos técnicos..."
                    />
                  </div>
                </div>
              </div>
              )}

              <Button
                onClick={handleBarSubmit}
                disabled={saving}
                className="w-full mt-6 btn-ruido bg-primary text-white font-mono"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                GUARDAR CAMBIOS
              </Button>
            </div>
          </TabsContent>

          {/* Requests Tab - Pending events from bands */}
          <TabsContent value="requests">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                SOLICITUDES DE BANDAS
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Eventos propuestos por bandas para tu local. Puedes aprobarlos o rechazarlos.
              </p>

              {pendingEvents.length > 0 ? (
                <div className="space-y-4">
                  {pendingEvents.map((event) => (
                    <div
                      key={event.event_id}
                      className="bg-[#0a0a0a] border border-zinc-800 p-4"
                    >
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* Band Info */}
                        <div className="flex items-start gap-3 md:w-1/3">
                          {event.band_image_url ? (
                            <img 
                              src={event.band_image_url} 
                              alt={event.band_name}
                              className="w-16 h-16 object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center">
                              <Music className="w-6 h-6 text-zinc-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-mono text-xs text-primary mb-1">BANDA</p>
                            <h4 className="font-bold text-white">{event.band_name || "Banda"}</h4>
                            {event.band_email && (
                              <p className="font-mono text-xs text-zinc-500">{event.band_email}</p>
                            )}
                            {event.band_music_genres?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {event.band_music_genres.slice(0, 3).map((genre) => (
                                  <span key={genre} className="bg-zinc-800 text-zinc-400 text-[9px] font-mono px-1 py-0.5">
                                    {genre.replace(/-/g, " ").toUpperCase()}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Event Info */}
                        <div className="flex-1">
                          <p className="font-mono text-xs text-zinc-500 mb-1">EVENTO PROPUESTO</p>
                          <h3 className="font-bold text-white text-lg">{event.title}</h3>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-zinc-400 text-sm">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {format(new Date(event.event_date), "EEEE d MMM yyyy", { locale: es })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {format(new Date(event.event_date), "HH:mm")}H
                            </span>
                            {event.price && (
                              <span className="text-primary font-mono">{event.price}</span>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-zinc-500 text-sm mt-2 line-clamp-2">{event.description}</p>
                          )}
                          {event.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {event.tags.map((tag) => (
                                <span key={tag} className="bg-primary/20 text-primary text-[10px] font-mono px-1.5 py-0.5 uppercase">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {event.ticket_url && (
                            <p className="font-mono text-xs text-zinc-500 mt-2">
                              <Ticket className="w-3 h-3 inline mr-1" />
                              Link de entradas incluido
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex md:flex-col gap-2 md:w-auto">
                          <Button
                            onClick={() => handleApproveEvent(event.event_id)}
                            disabled={approvingEvent === event.event_id}
                            className="flex-1 md:flex-none btn-ruido bg-green-600 hover:bg-green-700 text-white font-mono text-xs"
                          >
                            {approvingEvent === event.event_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                APROBAR
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleRejectEvent(event.event_id)}
                            disabled={approvingEvent === event.event_id}
                            variant="outline"
                            className="flex-1 md:flex-none border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-mono text-xs"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            RECHAZAR
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border border-zinc-800 bg-[#0a0a0a]">
                  <Bell className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">
                    NO HAY SOLICITUDES PENDIENTES
                  </p>
                  <p className="text-zinc-600 text-xs mt-2">
                    Las bandas pueden proponer eventos para tu local
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="space-y-8">
              {/* Header Section */}
              <div className="text-center py-6">
                <h2 className="text-3xl font-black text-white mb-3">ELIGE TU PLAN</h2>
                <p className="text-zinc-400 max-w-xl mx-auto">
                  Lleva tu bar al siguiente nivel. Más visibilidad, más conexiones, más conciertos.
                </p>
                {bar?.subscription_plan === "pro" && (
                  <div className="mt-4 inline-flex items-center gap-2 bg-primary/20 border border-primary/30 px-4 py-2">
                    <Crown className="w-4 h-4 text-primary" />
                    <span className="text-primary font-mono text-sm">
                      BACKSTAGE activo desde {bar?.subscription_start ? format(new Date(bar.subscription_start), "d MMM yyyy", { locale: es }) : "N/A"}
                    </span>
                  </div>
                )}
              </div>

              {/* Plans Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                
                {/* GARAGE Plan */}
                <div className={`relative group ${
                  bar?.subscription_plan === "free" || !bar?.subscription_plan
                    ? "ring-2 ring-primary" 
                    : ""
                }`}>
                  <div className="absolute inset-0 bg-gradient-to-b from-zinc-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  <div className="bg-[#0d0d0d] border border-zinc-800 p-8 h-full relative">
                    {/* Current Plan Badge */}
                    {(bar?.subscription_plan === "free" || !bar?.subscription_plan) && (
                      <div className="absolute -top-3 left-6 bg-zinc-700 text-white text-xs font-mono px-3 py-1">
                        TU PLAN ACTUAL
                      </div>
                    )}
                    
                    {/* Plan Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-zinc-800 flex items-center justify-center">
                          <Music className="w-6 h-6 text-zinc-400" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-black text-white">GARAGE</h3>
                          <p className="text-zinc-500 text-sm font-mono">Para empezar</p>
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-8">
                      <div className="flex items-baseline gap-1">
                        <span className="text-5xl font-black text-white">0€</span>
                        <span className="text-zinc-500 font-mono">/mes</span>
                      </div>
                      <p className="text-zinc-600 text-sm mt-1">Gratis para siempre</p>
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider">Incluye:</p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-sm">Hasta <strong className="text-white">2 eventos</strong> mensuales</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-sm">Perfil completo con foto, dirección y horarios</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-sm">Ubicación en el mapa de bares</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-sm">Visible en búsquedas por ciudad</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <X className="w-5 h-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-500 text-sm line-through">Link a venta de entradas</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <X className="w-5 h-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-500 text-sm line-through">Conexión con bandas</span>
                        </li>
                      </ul>
                    </div>

                    {/* CTA */}
                    {bar?.subscription_plan === "pro" && (
                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/bars/me/subscription`, { plan: "free" }, { headers: getAuthHeaders() });
                            toast.success("PLAN ACTUALIZADO A GARAGE");
                            fetchData();
                          } catch (e) {
                            toast.error("ERROR AL CAMBIAR PLAN");
                          }
                        }}
                        variant="outline"
                        className="w-full h-12 border-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-mono"
                        data-testid="select-free-plan"
                      >
                        CAMBIAR A GARAGE
                      </Button>
                    )}
                  </div>
                </div>

                {/* BACKSTAGE Pro Plan */}
                <div className={`relative group ${
                  bar?.subscription_plan === "pro" ? "ring-2 ring-primary" : ""
                }`}>
                  {/* Glow effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/50 to-orange-500/50 blur opacity-30 group-hover:opacity-50 transition-opacity" />
                  
                  <div className="bg-[#0d0d0d] border border-primary/50 p-8 h-full relative">
                    {/* Recommended Badge */}
                    <div className="absolute -top-3 right-6 bg-primary text-white text-xs font-mono px-3 py-1 flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      RECOMENDADO
                    </div>
                    
                    {/* Current Plan Badge */}
                    {bar?.subscription_plan === "pro" && (
                      <div className="absolute -top-3 left-6 bg-primary text-white text-xs font-mono px-3 py-1">
                        TU PLAN ACTUAL
                      </div>
                    )}
                    
                    {/* Plan Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-primary/20 flex items-center justify-center">
                          <Crown className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-black text-white">BACKSTAGE</h3>
                          <p className="text-primary text-sm font-mono">Plan Pro</p>
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-8">
                      <div className="flex items-baseline gap-1">
                        <span className="text-5xl font-black text-white">29€</span>
                        <span className="text-zinc-500 font-mono">/mes</span>
                      </div>
                      <div className="mt-2 inline-block bg-green-500/20 border border-green-500/30 px-3 py-1">
                        <span className="text-green-400 text-sm font-mono">🎉 GRATIS hasta 01/05/2026</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider">Todo de Garage, más:</p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white text-sm font-medium">Eventos <strong>ILIMITADOS</strong> cada mes</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white text-sm font-medium">Link directo a venta de entradas</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white text-sm font-medium">Matching con bandas por estilo musical</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white text-sm font-medium">Gestión de solicitudes y calendario</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white text-sm font-medium">Panel de Analytics con métricas</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white text-sm font-medium">Posicionamiento destacado</span>
                        </li>
                      </ul>
                    </div>

                    {/* CTA */}
                    {bar?.subscription_plan !== "pro" && (
                      <Button
                        onClick={async () => {
                          setProcessingPayment(true);
                          try {
                            const res = await axios.post(`${API}/subscription/checkout`, {
                              origin_url: window.location.origin
                            }, { headers: getAuthHeaders() });
                            window.location.href = res.data.checkout_url;
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "ERROR AL PROCESAR PAGO");
                            setProcessingPayment(false);
                          }
                        }}
                        disabled={processingPayment}
                        className="w-full h-12 bg-primary hover:bg-red-700 text-white font-mono font-bold text-base border-0"
                        data-testid="select-pro-plan"
                      >
                        {processingPayment ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            PROCESANDO...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5 mr-2" />
                            ACTIVAR BACKSTAGE
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Note */}
              <div className="text-center space-y-2">
                <p className="text-zinc-500 font-mono text-xs">
                  Pago seguro con Stripe • Cancela cuando quieras • Sin permanencia
                </p>
                <p className="text-zinc-600 text-xs">
                  ¿Tienes dudas? Escríbenos a <a href="mailto:info@rockmaps.es" className="text-primary hover:underline">info@rockmaps.es</a>
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab - PRO only */}
          {bar?.subscription_plan === "pro" && (
            <TabsContent value="analytics">
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-[#111] border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-black text-white flex items-center gap-2">
                      <BarChart3 className="w-6 h-6 text-primary" />
                      ANALYTICS
                    </h2>
                    <span className="bg-primary/20 text-primary font-mono text-xs px-3 py-1">
                      ÚLTIMOS 30 DÍAS
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Métricas de visitas, interacciones y rendimiento de tus eventos.
                  </p>
                </div>

                {loadingAnalytics ? (
                  <div className="bg-[#111] border border-zinc-800 p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                    <p className="text-zinc-500 font-mono text-sm">CARGANDO ANALYTICS...</p>
                  </div>
                ) : analytics ? (
                  <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-[#111] border border-zinc-800 p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-blue-400" />
                          </div>
                          <span className="font-mono text-xs text-zinc-500">VISITAS</span>
                        </div>
                        <p className="text-3xl font-black text-white">{analytics.total_views}</p>
                        <p className="text-zinc-600 text-xs font-mono mt-1">a tu perfil de bar</p>
                      </div>

                      <div className="bg-[#111] border border-zinc-800 p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
                            <Music className="w-5 h-5 text-purple-400" />
                          </div>
                          <span className="font-mono text-xs text-zinc-500">EVENTOS</span>
                        </div>
                        <p className="text-3xl font-black text-white">{analytics.event_views}</p>
                        <p className="text-zinc-600 text-xs font-mono mt-1">vistas a eventos</p>
                      </div>

                      <div className="bg-[#111] border border-zinc-800 p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                            <MousePointer className="w-5 h-5 text-green-400" />
                          </div>
                          <span className="font-mono text-xs text-zinc-500">CLICKS</span>
                        </div>
                        <p className="text-3xl font-black text-white">{analytics.ticket_clicks}</p>
                        <p className="text-zinc-600 text-xs font-mono mt-1">en entradas</p>
                      </div>

                      <div className="bg-[#111] border border-zinc-800 p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-red-500/20 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-red-400" />
                          </div>
                          <span className="font-mono text-xs text-zinc-500">FAVORITOS</span>
                        </div>
                        <p className="text-3xl font-black text-white">{analytics.favorites_count}</p>
                        <p className="text-zinc-600 text-xs font-mono mt-1">usuarios</p>
                      </div>
                    </div>

                    {/* Daily Views Chart */}
                    <div className="bg-[#111] border border-zinc-800 p-6">
                      <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        VISITAS DIARIAS (7 DÍAS)
                      </h3>
                      <div className="flex items-end gap-2 h-32">
                        {analytics.daily_views?.map((day, index) => {
                          const maxViews = Math.max(...analytics.daily_views.map(d => d.views), 1);
                          const height = (day.views / maxViews) * 100;
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center gap-1">
                              <div 
                                className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                                style={{ height: `${Math.max(height, 5)}%` }}
                                title={`${day.views} visitas`}
                              />
                              <span className="text-[10px] font-mono text-zinc-500">{day.date}</span>
                            </div>
                          );
                        })}
                      </div>
                      {analytics.daily_views?.every(d => d.views === 0) && (
                        <p className="text-zinc-600 text-xs font-mono text-center mt-4">
                          Aún no hay datos de visitas. Las métricas se actualizarán cuando los usuarios visiten tu perfil.
                        </p>
                      )}
                    </div>

                    {/* Events Performance */}
                    <div className="bg-[#111] border border-zinc-800 p-6">
                      <h3 className="text-lg font-black text-white mb-4">RENDIMIENTO POR EVENTO</h3>
                      {analytics.events?.length > 0 ? (
                        <div className="space-y-3">
                          {analytics.events.map((event) => (
                            <div key={event.event_id} className="bg-[#0a0a0a] border border-zinc-800 p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-bold text-white">{event.title}</h4>
                                  <p className="text-zinc-500 text-xs font-mono">
                                    {format(new Date(event.event_date), "d MMM yyyy", { locale: es })}
                                  </p>
                                </div>
                                <div className="flex items-center gap-6">
                                  <div className="text-center">
                                    <p className="text-xl font-black text-white">{event.views}</p>
                                    <p className="text-zinc-600 text-[10px] font-mono">VISTAS</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xl font-black text-green-400">{event.ticket_clicks}</p>
                                    <p className="text-zinc-600 text-[10px] font-mono">CLICKS</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Music className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                          <p className="text-zinc-500 font-mono text-sm">NO HAY EVENTOS PRÓXIMOS</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bg-[#111] border border-zinc-800 p-12 text-center">
                    <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 font-mono text-sm">NO HAY DATOS DE ANALYTICS</p>
                    <p className="text-zinc-600 text-xs mt-2">Las métricas aparecerán cuando los usuarios interactúen con tu perfil.</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
